// Eskom PARCC — compliance distribution donut (recharts).
//
// Theme-aware: slice colours are passed in (resolved from CSS tokens by the
// caller via useThemeColors), and the centre label shows the total. Interactive
// tooltip on hover. Pure presentational — consumes already-derived data.
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { ChartFrame } from './ChartFrame';

export interface DonutDatum {
  name: string;
  value: number;
  colour: string;
}

export function DistributionDonut({
  data,
  centerValue,
  centerLabel,
  height = 200,
}: {
  data: DonutDatum[];
  centerValue: string;
  centerLabel: string;
  height?: number;
}): React.JSX.Element {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div style={{ position: 'relative', width: '100%', height }}>
      <ChartFrame>
        {({ width, height: h }) => (
          <PieChart width={width} height={h}>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius="62%"
              outerRadius="92%"
              paddingAngle={1.5}
              stroke="none"
              startAngle={90}
              endAngle={-270}
            >
              {data.map((d) => (
                <Cell key={d.name} fill={d.colour} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => {
                const v = Number(value);
                return [`${v.toLocaleString('en-ZA')} (${total ? Math.round((v / total) * 100) : 0}%)`, name];
              }}
            />
          </PieChart>
        )}
      </ChartFrame>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          pointerEvents: 'none',
          textAlign: 'center',
        }}
      >
        <div>
          <div className="tnum" style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>
            {centerValue}
          </div>
          <div
            style={{
              fontSize: 'var(--fs-2xs)',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--faint)',
              fontWeight: 700,
            }}
          >
            {centerLabel}
          </div>
        </div>
      </div>
    </div>
  );
}
